import reading from './lucienPropertiesRows.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { difference, intersection } from '../../../geo/clip';
import { polygonArea, ringBounds } from '../../../geo/polygon';
import { footprintInside, lotsOverlap, obliqueCorners } from '../lotFootprint';
import { obbFromFrontEdge } from '../lotObb';
import { lotFrame } from '../lotFrame';
import type { Lot, ReservedFootprint } from '../types';
import { obliqueRowLots, type ObliqueRow } from './obliqueRowLots';

export const LUCIEN_PROPERTY_ROWS = reading.rows as unknown as readonly ObliqueRow[];

function behind(lot: Lot): Lot {
  const f = lotFrame(lot),
    right: Vec2 = [-f.facing[1], f.facing[0]],
    width = f.width - 1,
    side = lot.ordinal % 2 ? -1 : 1,
    front: Vec2 = [
      f.frontMid[0] - f.facing[0] * (f.depth + 0.03) + right[0] * side * 0.5,
      f.frontMid[1] - f.facing[1] * (f.depth + 0.03) + right[1] * side * 0.5,
    ],
    corners = obliqueCorners(front, f.facing, width, 3, 0);
  return {
    ...lot,
    kind: 'backhouse',
    depth: 3,
    frontage: width,
    obb: obbFromFrontEdge(corners[0], corners[1], 3, f.facing),
    corners,
    plot: { frontMid: front, shear: 0 },
    reconstruction: {
      ...lot.reconstruction!,
      id: `${lot.reconstruction!.id}/rear-room`,
      params: {
        storeys: 1,
        storeyHeight: 2.45,
        pitch: Math.PI / 6,
        roofMaterial: 'tile',
        frontGable: 'plain',
        backGable: 'plain',
        chimneys: [],
      },
    },
  };
}

function cowHouse(): Lot {
  const p = reading.cowHouse,
    angle = (p.headingDeg * Math.PI) / 180,
    facing: Vec2 = [Math.cos(angle), Math.sin(angle)],
    front: Vec2 = [
      p.center[0]! + (facing[0] * p.length) / 2,
      p.center[1]! + (facing[1] * p.length) / 2,
    ],
    corners = obliqueCorners(front, facing, p.width, p.length, 0);
  return {
    index: 0,
    sourceId: 'street-kalverstraat',
    side: 'left',
    ordinal: 50_999,
    kind: 'backhouse',
    depth: p.length,
    frontage: p.width,
    facing,
    corners,
    obb: obbFromFrontEdge(corners[0], corners[1], p.length, facing),
    reconstruction: {
      id: 'lucien-cow-house-before-1591',
      row: 'lucien-rear-ground',
      evidence: 'inferred',
      params: {
        storeys: 1,
        storeyHeight: p.storeyHeight,
        pitch: 0.7,
        roofMaterial: 'tile',
        frontGable: 'plain',
        backGable: 'plain',
        frontType: 'timber',
        chimneys: [],
      },
    },
  };
}

/** Deed-backed rental properties remain houses; rear ground belongs to the convent. */
export function planLucienProperties(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  if (
    ['street-sint-luciensteeg-2', 'street-kalverstraat'].some(
      (id) => !ctx.layout.streets.some((s) => s.id === id)
    )
  )
    return { lots: [], reserved: [], unresolved: [] };
  const lots: Lot[] = obliqueRowLots(
    LUCIEN_PROPERTY_ROWS,
    ctx.layout,
    buildable,
    reserved,
    50_000
  ).map((lot) => ({
    ...lot,
    reconstruction: {
      ...lot.reconstruction!,
      params: {
        ...lot.reconstruction!.params,
        storeyHeight: lot.reconstruction!.row === 'lucien-kamers' ? 3.35 : 4.1,
      },
    },
  }));
  const expected = LUCIEN_PROPERTY_ROWS.flatMap((r) => r.houses.map((h) => h.id));
  const reservations = reserved.map((r) => ({
    polygon: r.polygon,
    box: ringBounds(r.polygon.outer),
  }));
  const allowed = (lot: Lot) => {
    if (!footprintInside(buildable, lot.corners)) return false;
    if (lots.some((other) => lotsOverlap(lot, other, 0.001))) return false;
    const box = ringBounds(lot.corners);
    return !reservations.some(
      ({ polygon, box: b }) =>
        b.maxX >= box.minX &&
        b.minX <= box.maxX &&
        b.maxY >= box.minY &&
        b.minY <= box.maxY &&
        intersection([{ outer: [...lot.corners], holes: [] }], [polygon]).some(
          (p) => polygonArea(p) > 0.001
        )
    );
  };
  for (const main of [...lots].filter((l) => l.reconstruction!.row === 'lucien-kamers')) {
    const rear = behind(main);
    expected.push(rear.reconstruction!.id);
    if (allowed(rear)) lots.push(rear);
  }
  const stable = cowHouse();
  expected.push(stable.reconstruction!.id);
  if (allowed(stable)) lots.push(stable);
  const built = new Set(lots.map((l) => l.reconstruction!.id));
  const plots: ReservedFootprint[] = lots.map((l) => ({
    id: `surveyed:${l.reconstruction!.id}`,
    polygon: { outer: [...l.corners], holes: [] },
  }));
  const ground = { outer: reading.privateGround.map((p) => [p[0]!, p[1]!] as Vec2), holes: [] };
  const dry = difference(
    [ground],
    [...ctx.site.water, ...ctx.site.streets, ...reserved.map((r) => r.polygon)]
  );
  plots.push(...dry.map((polygon, i) => ({ id: `lucien-private-ground-${i}`, polygon })));
  return { lots, reserved: plots, unresolved: expected.filter((id) => !built.has(id)) };
}
